import type { ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { updateLevelMutation } from '~/util/mutations/staff/levels';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { levelEditFormInput, levelEditFormSchema } from '~/util/forms/level';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { levelCreatorsQuery } from '~/util/queries/public/levels';
import { Suspense, useEffect } from 'react';
import Load from '~/components/Load';
import { Info } from 'lucide-react';

const Route = getRouteApi('/list/$levelid');

const EditLevel = (modalProps: ModalProps) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const level = Route.useLoaderData();
    const { data: creators } = useSuspenseQuery(
        levelCreatorsQuery({ list, levelid }),
    );

    const mutation = useApiMutation({
        ...updateLevelMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'edit-level-staff',
            defaultValues: {
                list,
                level,
                creators,
            } as levelEditFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelEditFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({ data: value, levelid });
            },
        }),
    );

    useEffect(() => {
        form.reset({
            list,
            level,
            creators,
        });
    }, [list, level, creators, form]);

    return (
        <>
            <form
                id="edit-level-staff"
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit();
                }}
            >
                <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                    <form.AppField name="level.name">
                        {(field) => (
                            <field.TextField
                                label="Name"
                                required
                                placeholder="Level name"
                            />
                        )}
                    </form.AppField>
                    <form.AppField name="level.description">
                        {(field) => (
                            <field.TextAreaField
                                classname="w-lg"
                                label="Description"
                                placeholder="Level description"
                            />
                        )}
                    </form.AppField>
                    <form.AppField name="level.level_id">
                        {(field) => (
                            <field.NumberField
                                label="Level ID"
                                required
                                placeholder="12345678"
                            />
                        )}
                    </form.AppField>
                    <form.AppField name="level.publisher">
                        {(field) => (
                            <field.UserField
                                label="Publisher"
                                required
                                tagOptions={{ showFullName: true }}
                            />
                        )}
                    </form.AppField>
                    <form.AppField name="creators">
                        {(field) => (
                            <field.UsersField
                                label="Creators"
                                required
                                tagOptions={{
                                    highContrast: true,
                                    showFullName: true,
                                }}
                            />
                        )}
                    </form.AppField>
                    <form.AppField name="level.tags">
                        {(field) => <field.LevelTagsField label="Tags" />}
                    </form.AppField>
                    <form.AppField name="level.song">
                        {(field) => (
                            <field.NumberField
                                label="Song ID"
                                placeholder="Newgrounds ID"
                                labelNotes="Leave blank for NONG"
                            />
                        )}
                    </form.AppField>
                    <form.AppField name="level.two_player">
                        {(field) => (
                            <field.CheckboxField
                                label="Two Player"
                                labelNotes="For Solo/2P levels, indicates whether this is the 2P version of the level if checked or the solo one if unchecked."
                            />
                        )}
                    </form.AppField>
                    <form.Subscribe>
                        {({
                            values: {
                                level: { status },
                            },
                        }) =>
                            status == 'Pending' && (
                                <form.AppField name="level.requires_raw_footage">
                                    {(field) => (
                                        <field.CheckboxField
                                            label="Requires Raw Footage"
                                            labelNotes="For Pending levels, whether or not submitting a record should require raw footage"
                                        />
                                    )}
                                </form.AppField>
                            )
                        }
                    </form.Subscribe>
                </div>

                <div className="rounded-xl outline-1 outline-white w-fit mx-4 p-2 ">
                    <Info className="inline w-5 mb-1 text-secondary-foreground" />{' '}
                    Note
                    <br />
                    Verification(s) can be edited on the{' '}
                    <Link
                        to="/staff/records"
                        search={{ level }}
                        className="underline text-blue-500 hover:text-blue-700"
                        target="_blank"
                    >
                        records page for this level
                    </Link>
                    .
                </div>

                <SheetFooter className="sm:justify-center">
                    <form.AppForm>
                        <form.SubscribeButton
                            label="Edit level"
                            classname="w-11/12"
                        />
                    </form.AppForm>
                </SheetFooter>
            </form>
        </>
    );
};

export const EditLevelSheet: React.FC<ModalProps> = (modalProps) => {
    return (
        <Sheet {...modalProps}>
            <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                <SheetHeader>
                    <SheetTitle>Edit level</SheetTitle>
                    <SheetDescription>
                        Update level information, creators list, tags, etc.
                    </SheetDescription>
                </SheetHeader>
                <Suspense fallback={<Load />}>
                    <EditLevel {...modalProps} />
                </Suspense>
            </SheetContent>
        </Sheet>
    );
};
